import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Star, Award, Truck, FileCheck, CheckCircle, AlertTriangle,
  RefreshCw, Loader2, ChevronRight, MoreHorizontal, Package,
} from 'lucide-react';

const getScoreColor = (score) => {
  if (score === null || score === undefined) return { bg: 'rgba(100,116,139,0.10)', text: '#64748B' };
  if (score >= 80) return { bg: 'rgba(34,197,94,0.10)', text: '#22C55E', border: 'rgba(34,197,94,0.30)' };
  if (score >= 60) return { bg: 'rgba(245,158,11,0.10)', text: '#F59E0B', border: 'rgba(245,158,11,0.30)' };
  return { bg: 'rgba(239,68,68,0.10)', text: '#EF4444', border: 'rgba(239,68,68,0.30)' };
};

const StarRating = ({ rating, onChange, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readOnly && setHover(star)}
          onMouseLeave={() => setHover(0)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <Star
            className="w-5 h-5 transition-colors"
            fill={(hover || rating) >= star ? '#C5A55A' : 'transparent'}
            stroke={(hover || rating) >= star ? '#C5A55A' : '#CBD5E1'}
          />
        </button>
      ))}
    </div>
  );
};

export default function VendorScorecardPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [ratingInput, setRatingInput] = useState(0);
  const [notesInput, setNotesInput] = useState('');
  const [savingRating, setSavingRating] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await api.get('/vendor-scores');
      setVendors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch vendor scores:', err);
      toast.error('Failed to load vendor scores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      await api.post('/vendor-scores/recalculate-all');
      toast.success('All vendor scores recalculated');
      await fetchVendors();
    } catch (err) {
      toast.error('Failed to recalculate scores');
    } finally {
      setRecalculating(false);
    }
  };

  const openVendorDetail = (vendor) => {
    setSelectedVendor(vendor);
    setRatingInput(vendor.manual_rating || 0);
    setNotesInput(vendor.notes || '');
    setDetailOpen(true);
  };

  const saveRating = async () => {
    if (!selectedVendor) return;
    setSavingRating(true);
    try {
      await api.put(`/vendor-scores/${encodeURIComponent(selectedVendor.vendor_name)}/rating`, {
        manual_rating: ratingInput,
        notes: notesInput,
      });
      toast.success('Rating saved');
      await fetchVendors();
      // Update selected vendor
      setSelectedVendor(prev => ({ ...prev, manual_rating: ratingInput, notes: notesInput }));
    } catch (err) {
      toast.error('Failed to save rating');
    } finally {
      setSavingRating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-80" />
      </div>
    );
  }

  // Summary stats
  const avgScore = vendors.length > 0
    ? Math.round(vendors.reduce((sum, v) => sum + (v.composite_score || 0), 0) / vendors.length)
    : 0;
  const greenVendors = vendors.filter(v => v.composite_score >= 80).length;
  const yellowVendors = vendors.filter(v => v.composite_score >= 60 && v.composite_score < 80).length;
  const redVendors = vendors.filter(v => v.composite_score < 60).length;

  return (
    <div className="space-y-6" data-testid="vendor-scorecard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold flex items-center gap-3" style={{ color: 'var(--brand-navy)' }}>
            <Award className="w-8 h-8" style={{ color: 'var(--brand-gold)' }} />
            Vendor Scorecard
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Performance metrics and ratings for all vendors
          </p>
        </div>
        <Button onClick={recalculateAll} disabled={recalculating} variant="outline" className="gap-2">
          {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Recalculate All
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Package className="w-4 h-4" />
              Total Vendors
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-navy)' }}>
              {vendors.length}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Star className="w-4 h-4" />
              Avg Score
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: getScoreColor(avgScore).text }}>
              {avgScore}%
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-green-200 bg-green-50 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-green-700">
              <CheckCircle className="w-4 h-4" />
              Excellent (≥80%)
            </div>
            <div className="text-2xl font-bold mt-1 text-green-600">
              {greenVendors}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-red-200 bg-red-50 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-red-700">
              <AlertTriangle className="w-4 h-4" />
              At Risk (&lt;60%)
            </div>
            <div className="text-2xl font-bold mt-1 text-red-600">
              {redVendors}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vendor Table */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="vendor-table">
        <CardContent className="p-0">
          {vendors.length === 0 ? (
            <p className="text-sm py-12 text-center" style={{ color: 'var(--ink-muted)' }}>
              No vendor data available. Scores are calculated from PO receipts and QC inspections.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">Vendor</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Delivery %</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Invoice %</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Quality %</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Rating</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Composite</TableHead>
                  <TableHead className="text-xs font-semibold text-center">POs</TableHead>
                  <TableHead className="text-xs font-semibold w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map(vendor => {
                  const scoreStyle = getScoreColor(vendor.composite_score);
                  return (
                    <TableRow key={vendor.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openVendorDetail(vendor)} data-testid={`vendor-row-${vendor.vendor_name}`}>
                      <TableCell>
                        <span className="font-medium text-sm" style={{ color: 'var(--brand-navy)' }}>
                          {vendor.vendor_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <MetricBadge value={vendor.delivery_reliability_pct} />
                      </TableCell>
                      <TableCell className="text-center">
                        <MetricBadge value={vendor.invoice_accuracy_pct} />
                      </TableCell>
                      <TableCell className="text-center">
                        <MetricBadge value={vendor.quality_pass_rate_pct} />
                      </TableCell>
                      <TableCell className="text-center">
                        <StarRating rating={vendor.manual_rating || 0} readOnly />
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="text-xs font-bold px-3 py-1" style={{
                          background: scoreStyle.bg,
                          color: scoreStyle.text,
                          border: `1px solid ${scoreStyle.border || 'transparent'}`,
                        }}>
                          {vendor.composite_score?.toFixed(0) || '-'}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm" style={{ color: 'var(--ink-muted)' }}>
                        {vendor.total_pos || 0}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Vendor Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="sm:max-w-[480px] bg-white overflow-y-auto" data-testid="vendor-detail-sheet">
          {selectedVendor && (
            <>
              <SheetHeader className="pb-4 border-b">
                <SheetTitle style={{ color: 'var(--brand-navy)' }}>{selectedVendor.vendor_name}</SheetTitle>
                <SheetDescription>Performance breakdown and manual rating</SheetDescription>
              </SheetHeader>

              <div className="space-y-6 py-4">
                {/* Composite Score */}
                <div className="text-center p-6 rounded-xl" style={{ background: getScoreColor(selectedVendor.composite_score).bg }}>
                  <div className="text-5xl font-bold" style={{ color: getScoreColor(selectedVendor.composite_score).text }}>
                    {selectedVendor.composite_score?.toFixed(0) || '-'}%
                  </div>
                  <div className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Composite Score</div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard
                    icon={Truck}
                    label="Delivery Reliability"
                    value={selectedVendor.delivery_reliability_pct}
                    sublabel={selectedVendor.avg_delivery_delay_days > 0 ? `Avg ${selectedVendor.avg_delivery_delay_days} days late` : 'On time'}
                  />
                  <MetricCard
                    icon={FileCheck}
                    label="Invoice Accuracy"
                    value={selectedVendor.invoice_accuracy_pct}
                    sublabel={`${selectedVendor.total_invoices || 0} invoices`}
                  />
                  <MetricCard
                    icon={CheckCircle}
                    label="Quality Pass Rate"
                    value={selectedVendor.quality_pass_rate_pct}
                    sublabel={`${selectedVendor.total_qc_inspections || 0} inspections`}
                  />
                  <MetricCard
                    icon={Package}
                    label="Total Orders"
                    value={null}
                    rawValue={`${selectedVendor.total_pos || 0} POs`}
                    sublabel={`${selectedVendor.total_line_items || 0} line items`}
                  />
                </div>

                {/* Manual Rating */}
                <div className="space-y-3 p-4 rounded-xl border border-border/80">
                  <Label className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Manual Rating</Label>
                  <StarRating rating={ratingInput} onChange={setRatingInput} />
                  <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
                  <Textarea
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                    placeholder="Add notes about this vendor..."
                    className="text-sm"
                    rows={3}
                  />
                  <Button onClick={saveRating} disabled={savingRating} className="w-full gap-2" style={{ background: 'var(--brand-navy)' }}>
                    {savingRating && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save Rating
                  </Button>
                </div>

                {/* Last Calculated */}
                <div className="text-xs text-center" style={{ color: 'var(--ink-muted)' }}>
                  Last calculated: {selectedVendor.last_calculated
                    ? new Date(selectedVendor.last_calculated).toLocaleString('en-IN')
                    : 'Never'}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MetricBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>-</span>;
  }
  const style = getScoreColor(value);
  return (
    <Badge variant="outline" className="text-xs" style={{ background: style.bg, color: style.text, borderColor: 'transparent' }}>
      {value.toFixed(0)}%
    </Badge>
  );
}

function MetricCard({ icon: Icon, label, value, sublabel, rawValue }) {
  const style = value !== null ? getScoreColor(value) : { bg: 'var(--surface-2)', text: 'var(--brand-navy)' };
  return (
    <div className="p-3 rounded-lg" style={{ background: style.bg }}>
      <div className="flex items-center gap-2 text-xs font-medium" style={{ color: style.text }}>
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-xl font-bold mt-1" style={{ color: style.text }}>
        {rawValue || (value !== null ? `${value?.toFixed(0)}%` : '-')}
      </div>
      <div className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{sublabel}</div>
    </div>
  );
}
